import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "step",
    "continueButton",
    "submitButton",
    "backButton",
    "resetButton",
    "otherField",
    "progress",
    "currentStep",
    "tasks",
    "categories",
    "pageCounter"
  ];

  connect() {
    this.currentStep = 0;
    this.isLovedOnePassed = false;
    this.lovedOnePassedInitialStep = 3;
    this.defaultFinalStep = 2;
    this.updateStepVisibility();
    this.updateButtons();
    this.updateCurrentStep();
  }

  onNext(event) {
    event.preventDefault();

    const currentStepElement = this.stepTargets[this.currentStep];
    if (!this.validateFieldsIn(currentStepElement)) {
      return;
    }

    this.currentStep++;

    if (this.isLovedOnePassed && this.currentStep === this.defaultFinalStep) {
      this.currentStep++;
    }

    this.updateStepState();

    if (this.currentStep < this.lovedOnePassedInitialStep) {
      this.fireProgressEvent();
    }
  }

  onBack(event) {
    event.preventDefault();

    if (this.currentStep < this.lovedOnePassedInitialStep) {
      this.progressEvent("progress:backStep");
    }

    if (this.isLovedOnePassed && this.currentStep === this.lovedOnePassedInitialStep) {
      this.currentStep--;
    }

    this.currentStep--;
    this.updateStepState();
  }

  updateStepState() {
    this.updateStepVisibility();
    this.updateButtons();
    this.updateCurrentStep();
  }

  onSubmit(event) {
    event.preventDefault();

    const currentStepElement = this.stepTargets[this.currentStep];
    if (!this.validateFieldsIn(currentStepElement)) {
      return;
    }

    this.fireProgressEvent();

    const form = event.target.closest("form");
    form.submit();
  }

  onCircumstanceChange(event) {
    const isLovedOnePassed = event.target.value === "loved_one_recently_passed";
    this.isLovedOnePassed = isLovedOnePassed;
    
    if (isLovedOnePassed) {
      this.resetTasks();
    } else {
      this.toggleElementVisibility(this.tasksTarget, true);
      this.updateButtons(false);
      this.showSelectedCategory(event.target.value);
    }
  }

  showSelectedCategory(value) {
    if (!value) return;

    this.categoriesTargets.forEach((category) => {
      const isSelected = category.dataset.circumstanceValue === value;
      this.toggleElementVisibility(category, isSelected);
      this.setCheckboxesState(category.querySelectorAll('input[type="checkbox"]'), isSelected);
    });
  }

  toggleOtherField(event) {
    this.toggleElementVisibility(this.otherFieldTarget, event.target.value === "other");
  }

  updateStepVisibility() {
    this.stepTargets.forEach((element, index) => {
      this.toggleElementVisibility(element, index === this.currentStep);
    });

    const showProgress = this.currentStep < 3;
    this.toggleElementVisibility(this.progressTarget, showProgress);
    this.toggleElementVisibility(this.pageCounterTarget, showProgress);
  }

  updateButtons() {
    this.defaultButtons();
    if (this.isLovedOnePassed) {
      this.lovedOnePassedButtons();
    }
  }

  defaultButtons() {
    const isOnFinalStep = this.currentStep === this.defaultFinalStep;
    this.toggleElementVisibility(this.continueButtonTarget, !isOnFinalStep);
    this.toggleElementVisibility(this.submitButtonTarget, isOnFinalStep);

    const isFirstStep = this.currentStep === 0;
    this.toggleElementVisibility(this.backButtonTarget, !isFirstStep);
    this.toggleElementVisibility(this.resetButtonTarget, isFirstStep);
  }

  lovedOnePassedButtons() {
    const lovedOneFinalStep = 4;

    if (this.currentStep === lovedOneFinalStep) {
      this.toggleElementVisibility(this.backButtonTarget, false);
      this.toggleElementVisibility(this.continueButtonTarget, false);
      this.toggleElementVisibility(this.submitButtonTarget, false);
    } else {
      this.toggleElementVisibility(this.backButtonTarget, this.currentStep > 0);
      this.toggleElementVisibility(this.continueButtonTarget, true);
    }
  }

  resetTasks() {
    this.toggleElementVisibility(this.tasksTarget, false);
    this.setCheckboxesState(this.tasksTarget.querySelectorAll('input[type="checkbox"]'), false);
  }

  updateCurrentStep() {
    this.currentStepTarget.textContent = this.currentStep + 2;
  }

  validateFieldsIn(stepElement) {
    const fields = stepElement.querySelectorAll("input, select, textarea");
    let valid = true;

    fields.forEach((field) => {
      if (!field.checkValidity()) {
        field.reportValidity();
        valid = false;
      }
    });
    return valid;
  }

  fireProgressEvent() {
    this.progressEvent("progress:nextStep");
  }

  progressEvent(eventType) {
    if (this.hasProgressTarget) {
      const event = new Event(eventType);
      this.progressTarget.dispatchEvent(event);
    }
  }

  submitWithParams(event) {
    event.preventDefault();
    
    const form = this.element.querySelector('form');
    if (!form) return;
    
    if (event.currentTarget.dataset.browseFuneralDirectors) {
      this.addHiddenField(form, 'browse_funeral_directors', event.currentTarget.dataset.browseFuneralDirectors);
    }
    
    if (event.currentTarget.dataset.alreadyHaveFuneralDirector) {
      this.addHiddenField(form, 'with_funeral_director', event.currentTarget.dataset.alreadyHaveFuneralDirector);
    }
    
    const formContainer = this.element.querySelector('[data-form-path]');
    if (formContainer) {
      form.action = formContainer.dataset.formPath;
    }
    
    form.submit();
  }
  
  addHiddenField(form, name, value) {
    let field = form.querySelector(`input[name="${name}"]`);

    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = name;
      form.appendChild(field);
    }

    field.value = value;
  }

  toggleElementVisibility(element, isVisible) {
    if (isVisible) {
      element.classList.remove("hidden");
    } else {
      element.classList.add("hidden");
    }
  }

  setCheckboxesState(checkboxes, checked) {
    checkboxes.forEach(checkbox => {
      checkbox.checked = checked;
    });
  }
}
